import React from 'react';
import { ExternalLink, FileText, Clock, CheckCircle } from 'lucide-react';
import { MY_AGED_CARE_APPLY_URL } from '../../components/branding/MyAgedCareApplyLink.tsx';

type ACATApplicationContext = 'aged care' | 'home care';

export interface ACATApplicationButtonProps {
    context?: ACATApplicationContext;
}

interface ACATContextInfo {
    title: string;
    description: string;
    benefits: string[];
}

const ACATApplicationButton = ({ context = "aged care" }: ACATApplicationButtonProps) => {
    const handleACATApplication = () => {
        window.open(MY_AGED_CARE_APPLY_URL, '_blank', 'noopener,noreferrer');
    };

    const getContextText = (): ACATContextInfo => {
        switch (context) {
            case "home care":
                return {
                    title: "Need an aged care needs assessment for Support at Home?",
                    description: "To access Support at Home, you need an aged care needs assessment. This determines your care needs and Support at Home classification.",
                    benefits: [
                        "Determines your care needs and classification",
                        "Required for government-subsidised Support at Home",
                        "Free assessment service",
                        "Can be done in your own home"
                    ]
                };
            case "aged care":
            default:
                return {
                    title: "Need an aged care needs assessment for residential care?",
                    description: "To access residential aged care, you need an aged care needs assessment. This determines your care needs and eligibility for government-subsidised care.",
                    benefits: [
                        "Determines your care needs and care level",
                        "Required for government-subsidised care",
                        "Free assessment service",
                        "Can be done in your own home or hospital"
                    ]
                };
        }
    };

    const contextInfo = getContextText();

    return (
        <div className="p-6 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                        <FileText className="w-6 h-6 text-white" />
                    </div>
                </div>
                <div className="flex-1">
                    <h3 className="text-lg font-semibold text-blue-900 mb-2">{contextInfo.title}</h3>
                    <p className="text-blue-800 mb-4">{contextInfo.description}</p>
                    
                    {/* Benefits List */}
                    <div className="mb-4">
                        <ul className="space-y-2">
                            {contextInfo.benefits.map((benefit, index) => (
                                <li key={index} className="flex items-center gap-2 text-sm text-blue-700">
                                    <CheckCircle size={16} className="text-blue-600 flex-shrink-0" />
                                    {benefit}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Application Button */}
                    <button
                        onClick={handleACATApplication}
                        className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold flex items-center gap-2"
                    >
                        <ExternalLink size={16} />
                        Apply via My Aged Care
                    </button>

                    {/* Additional Info */}
                    <div className="mt-4 p-3 bg-blue-100 rounded-lg">
                        <div className="flex items-center gap-2 text-sm text-blue-800">
                            <Clock size={16} />
                            <span><strong>Assessment Process:</strong> Usually takes 2-4 weeks from application to assessment completion</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ACATApplicationButton;
